import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import {
  Assessment,
  AssessmentQuestion,
  AssessmentSection,
  demoAnswer,
} from '../../../support/assessments';
import { getAssessmentLayout } from '../../Assessments/layouts';

/**
 * A whole-assessment "preview prior to publishing": renders the assessment's
 * actual fill layout (the same skin respondents see, chosen by
 * `metadata.layout`) inside a full-screen dialog, seeded with demo answers so
 * every visible question renders an example. `previewMode` disables the
 * side-effecting inputs (uploads, Salesforce, data import) so previewing can't
 * create datasets or hit external services. It never submits — Exit/Submit just
 * close the preview.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  assessment: Assessment;
  sections: AssessmentSection[];
  questionsBySection: Record<number, AssessmentQuestion[]>;
  allQuestions: AssessmentQuestion[];
};

const AssessmentPreviewDialog: React.FC<Props> = ({
  open,
  onClose,
  assessment,
  sections,
  questionsBySection,
  allQuestions,
}) => {
  const Layout = getAssessmentLayout(assessment);

  const seed = useMemo(() => {
    const map: Record<string, any> = {};
    allQuestions.forEach((q) => {
      map[q.key] = demoAnswer(q);
    });
    return map;
  }, [allQuestions]);

  const [answers, setAnswers] = useState<Record<string, any>>(seed);

  // Reset to fresh demo answers each time the dialog opens.
  useEffect(() => {
    if (open) setAnswers(seed);
  }, [open, seed]);

  const answerFor = (q: AssessmentQuestion) => answers[q.key];
  const onChangeFor = (q: AssessmentQuestion) => (value: any) =>
    setAnswers((a) => ({ ...a, [q.key]: value }));

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Layout
        assessment={assessment}
        sections={sections}
        questionsBySection={questionsBySection}
        allQuestions={allQuestions}
        answers={answers}
        respondent={null}
        answerFor={answerFor}
        onChangeFor={onChangeFor}
        submitError={null}
        submitting={false}
        submitted={false}
        result={null}
        report={null}
        aiEraReport={null}
        aiEraProfile={null}
        onSubmit={onClose}
        onExit={onClose}
        previewMode
      />
    </Dialog>
  );
};

export default AssessmentPreviewDialog;
